import { loginSchema } from '@bqcrm/shared';
import { Alert, Anchor, Box, Button, Center, Checkbox, Divider, Group, Paper, PasswordInput, Stack, Text, TextInput, ThemeIcon, Title } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconFingerprint, IconLeaf } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, errorText, HttpError } from '../api';
import { mfaCodeOk } from '../components/MfaSettings';
import { AppFooter } from '../components/AppFooter';
import { loginWithPasskey, passkeyError, passkeysSupported, usePasskeyLoginOptions } from '../components/Passkeys';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  // środowisko demonstracyjne — baner już na stronie logowania
  const env = useQuery({ queryKey: ['environment'], queryFn: () => api<{ demo: boolean; label: string }>('/environment'), staleTime: Infinity, retry: false });
  const navigate = useNavigate();
  const location = useLocation();

  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from && from !== '/logowanie' ? from : '/', { replace: true });
  };
  const [pkBusy, setPkBusy] = useState(false);
  const pkOptions = usePasskeyLoginOptions(passkeysSupported());
  const withPasskey = async () => {
    setError(null);
    setPkBusy(true);
    try {
      await loginWithPasskey(pkOptions.data, remember);
      await done();
    } catch (err) {
      setError(passkeyError(err));
    } finally {
      setPkBusy(false);
      // wyzwanie jednorazowe — kolejna próba z nowym
      void qc.invalidateQueries({ queryKey: ['passkey-login-options'] });
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = loginSchema.safeParse({
      email,
      password,
      code: needCode ? code : undefined,
      remember,
    });
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join('\n'));
      return;
    }
    setBusy(true);
    try {
      await api('/auth/login', { method: 'POST', body: parsed.data });
      await done();
    } catch (err) {
      const mfa = err instanceof HttpError && (err.data as { mfaRequired?: boolean } | undefined)?.mfaRequired;
      if (mfa && !needCode) {
        // hasło poprawne — drugi krok: kod z aplikacji
        setNeedCode(true);
        setError(null);
      } else {
        setError(errorText(err));
        if (mfa) setCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      mih="100vh"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      }}
      className="login-grid"
    >
      <Box
        visibleFrom="md"
        p={48}
        style={{
          background: 'linear-gradient(140deg, var(--mantine-color-eko-9) 0%, var(--mantine-color-eko-7) 55%, #0f766e 100%)',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Group gap="sm">
          <ThemeIcon size={44} radius="md" color="white" variant="white">
            <IconLeaf size={24} color="var(--mantine-color-eko-8)" />
          </ThemeIcon>
          <div>
            <Text fw={800} size="xl" style={{ letterSpacing: '-0.02em' }}>
              Eko CRM
            </Text>
            <Text size="sm" opacity={0.8}>
              instalacje i serwis
            </Text>
          </div>
        </Group>
        <Stack gap="lg" maw={440}>
          <Title order={1} c="white" fz={34} lh={1.2}>
            Od zapytania, przez montaż, do przeglądu — w jednym miejscu.
          </Title>
          <Stack gap="xs">
            {['Sprawy, oferty i umowy kilku firm', 'Plan pracy ekip i aplikacja mobilna', 'Protokoły, urządzenia i przeglądy'].map((t) => (
              <Group key={t} gap="xs">
                <IconCircleCheck size={18} />
                <Text size="sm">{t}</Text>
              </Group>
            ))}
          </Stack>
        </Stack>
        <Text size="xs" opacity={0.7}>
          Pompy ciepła · klimatyzacja · wentylacja · ogrzewanie podłogowe · fotowoltaika
        </Text>
      </Box>
      <Center px="md" style={{ background: 'var(--app-bg)', gridColumn: 'auto' }}>
        <Stack w="100%" maw={400} gap="lg">
          <Group gap="sm" hiddenFrom="md">
            <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: 'eko.6', to: 'teal.8', deg: 135 }}>
              <IconLeaf size={22} />
            </ThemeIcon>
            <Text fw={800} size="lg">
              Eko CRM
            </Text>
          </Group>
          <div>
            {env.data?.demo && (
              <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} mb="sm">
                {env.data.label || 'Środowisko demonstracyjne — dane fikcyjne'}
              </Alert>
            )}
            <Title order={2}>Zaloguj się</Title>
            <Text c="dimmed" size="sm">
              Wpisz login (albo e-mail) i hasło.
            </Text>
          </div>
          <Paper withBorder shadow="sm" p="xl">
            <form onSubmit={submit} noValidate>
              <Stack>
                {(location.state as { passwordReset?: boolean } | null)?.passwordReset && !error && (
                  <Alert color="teal" icon={<IconCircleCheck />}>
                    Hasło zostało zmienione — zaloguj się nowym hasłem.
                  </Alert>
                )}
                {error && (
                  <Alert color="red" icon={<IconAlertTriangle />} style={{ whiteSpace: 'pre-line' }}>
                    {error}
                  </Alert>
                )}
                <TextInput label="Login" description="albo e-mail" autoComplete="username" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.currentTarget.value)} required autoFocus size="md" />
                <PasswordInput label="Hasło" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} required size="md" disabled={needCode} />
                {needCode && (
                  <Stack gap={4}>
                    <TextInput
                      key={recovery ? 'zapasowy' : 'totp'}
                      label={recovery ? 'Kod zapasowy' : 'Kod z aplikacji uwierzytelniającej'}
                      description={recovery ? 'Jeden z kodów zapisanych przy włączaniu logowania dwuetapowego, np. ABCD-EFGH' : '6 cyfr z aplikacji (np. Google Authenticator, Microsoft Authenticator)'}
                      autoComplete={recovery ? 'off' : 'one-time-code'}
                      inputMode={recovery ? undefined : 'numeric'}
                      autoCapitalize={recovery ? 'characters' : undefined}
                      spellCheck={false}
                      value={code}
                      onChange={(e) => setCode(e.currentTarget.value)}
                      autoFocus
                      size="md"
                      maxLength={recovery ? 11 : 6}
                    />
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      ta="left"
                      onClick={() => {
                        setRecovery(!recovery);
                        setCode('');
                      }}
                    >
                      {recovery ? 'Mam kod z aplikacji' : 'Mam kod zapasowy'}
                    </Anchor>
                  </Stack>
                )}
                <Checkbox
                  label="Zapamiętaj mnie"
                  description={remember ? 'Zostaniesz zalogowany na tym urządzeniu do 30 dni od ostatniej aktywności. Nie zaznaczaj na wspólnym komputerze.' : 'Bez zaznaczenia sesja kończy się po zamknięciu przeglądarki.'}
                  checked={remember}
                  onChange={(e) => setRemember(e.currentTarget.checked)}
                  disabled={needCode}
                />
                <Button type="submit" loading={busy} fullWidth size="md" disabled={needCode && !mfaCodeOk(code, recovery)}>
                  {needCode ? 'Potwierdź kod' : 'Zaloguj'}
                </Button>
                {needCode && (
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => {
                      setNeedCode(false);
                      setCode('');
                      setRecovery(false);
                      setPassword('');
                      setError(null);
                    }}
                  >
                    Zaloguj na inne konto
                  </Button>
                )}
                {!needCode && passkeysSupported() && (
                  <>
                    <Divider label="albo" labelPosition="center" />
                    <Button variant="default" fullWidth size="md" leftSection={<IconFingerprint size={18} />} loading={pkBusy} onClick={() => void withPasskey()}>
                      Zaloguj kluczem dostępu
                    </Button>
                  </>
                )}
              </Stack>
            </form>
          </Paper>
          <ForgotPassword initial={email} />
          <Text size="sm" c="dimmed" ta="center">
            Instalator lub serwisant? <Anchor href="/teren">Otwórz aplikację mobilną</Anchor>
          </Text>
          <AppFooter />
        </Stack>
      </Center>
    </Box>
  );
}

/**
 * „Nie pamiętasz hasła?” — login albo e-mail; system wysyła na e-mail konta link do ustawienia nowego hasła (ważny 60 minut).
 * Odpowiedź zawsze ta sama — nie zdradza, czy konto istnieje.
 */
function ForgotPassword({ initial }: { initial: string }) {
  const [open, setOpen] = useState(false);
  const [login, setLogin] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open)
    return (
      <Text size="sm" ta="center">
        <Anchor
          component="button"
          type="button"
          onClick={() => {
            setLogin(initial);
            setOpen(true);
          }}
        >
          Nie pamiętasz hasła?
        </Anchor>
      </Text>
    );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!login.trim()) return setError('Podaj login albo e-mail');
    setBusy(true);
    setError(null);
    try {
      await api('/auth/password-reset/request', { method: 'POST', body: { login: login.trim() } });
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Paper withBorder p="lg">
      {sent ? (
        <Stack gap="sm">
          <Alert color="teal" icon={<IconCircleCheck />}>
            Jeśli konto istnieje i ma adres e-mail, wysłaliśmy na niego link do ustawienia nowego hasła. Link jest ważny 60 minut.
          </Alert>
          <Text size="sm" c="dimmed">
            Nie dostałeś wiadomości? Sprawdź folder spam albo poproś administratora o ustawienie hasła.
          </Text>
          <Anchor component="button" type="button" size="sm" ta="left" onClick={() => (setOpen(false), setSent(false))}>
            Wróć do logowania
          </Anchor>
        </Stack>
      ) : (
        <form onSubmit={submit} noValidate>
          <Stack gap="sm">
            <Text fw={600}>Nie pamiętasz hasła?</Text>
            <Text size="sm" c="dimmed">
              Podaj login albo e-mail — wyślemy link do ustawienia nowego hasła.
            </Text>
            {error && <Alert color="red">{error}</Alert>}
            <TextInput label="Login" description="albo e-mail" autoComplete="username" autoCapitalize="none" value={login} onChange={(e) => setLogin(e.currentTarget.value)} autoFocus />
            <Group justify="space-between">
              <Anchor component="button" type="button" size="sm" onClick={() => setOpen(false)}>
                Anuluj
              </Anchor>
              <Button type="submit" loading={busy}>
                Wyślij link
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Paper>
  );
}
